import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarPlus, Info, KeyRound, PackageCheck, RefreshCw, ShieldOff } from 'lucide-react';
import { Popconfirm, Tabs, toast } from '../../../ui';
import fulfillmentService from '../../../services/fulfillmentService';
import { formatDateTime, formatINR } from '../../../utils/format';
import EmptyState from '../../../components/site/EmptyState';
import { AdminHeader, LoadError, Loading, StatTile, lookup } from './orderAdmin';

// Values of FulfillmentStatus, plus "NotDelivered" for paid orders from before delivery tracking.
const STATUS_META = {
  NeedsAttention: { label: 'Needs attention', tone: 'danger' },
  Failed: { label: 'Failed, retrying', tone: 'danger' },
  PartiallyDelivered: { label: 'Partly delivered', tone: 'warning' },
  Pending: { label: 'Pending', tone: 'warning' },
  NotDelivered: { label: 'Never delivered', tone: 'danger' },
};

const ACTORS = { admin: 'needs you', customer: 'customer', system: 'retries automatically' };

const ACCESS_STATUS_META = {
  Expired: { label: 'Expired, waiting for removal', tone: 'warning' },
  RemovalFailed: { label: 'Removal failed', tone: 'danger' },
  Removed: { label: 'Removed' },
};

/**
 * Orders whose access expired (waiting for removal or removal failed) or was removed in the last 30 days.
 * With automatic removal off (Delivery:AutoRevokeExpiredAccess=false) this is where expired access is removed.
 */
const AccessChanges = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null); // `${orderId}:revoke` | `${orderId}:extend`

  const load = useCallback(async () => {
    const result = await fulfillmentService.getAccessChanges(200);
    if (result.success) {
      setData(result.data || { autoRevoke: true, count: 0, waitingForRemoval: 0, failed: 0, items: [] });
      setError('');
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (key, call) => {
    setBusy(key);
    const result = await call();
    setBusy(null);
    if (result.success) toast.success(result.message || 'Done');
    else {
      const details = (result.data?.errors || []).filter(Boolean).join(' ');
      toast.error({ content: [result.error || result.message, details].filter(Boolean).join(' '), duration: 8 });
    }
    load();
  };

  if (loading) return <Loading label="Loading expired access" />;
  if (error) return <LoadError title="Couldn't load expired access" message={error} onRetry={load} />;

  const items = data?.items || [];

  return (
    <div className="oa-stack">
      <div className="pk-alert" data-tone={data?.autoRevoke ? 'info' : 'warning'} role="note">
        <Info aria-hidden="true" />
        <div>
          {data?.autoRevoke
            ? 'Automatic removal is on: expired access is removed every hour and the customer is emailed. Failed removals are retried each hour; you can also retry them here.'
            : 'Automatic removal is off (Delivery:AutoRevokeExpiredAccess). Expired access stays until you remove it here.'}
        </div>
      </div>

      <div className="oa-stats">
        <StatTile label="Waiting for removal" value={data?.waitingForRemoval ?? 0} attention={!data?.autoRevoke && data?.waitingForRemoval > 0} />
        <StatTile label="Removal failed" value={data?.failed ?? 0} attention={data?.failed > 0} />
        <StatTile label="Listed" value={data?.count ?? 0} hint="Includes removals in the last 30 days" />
      </div>

      {items.length === 0 ? (
        <EmptyState icon={<KeyRound />} title="No expired access" text="Orders show up here when their access period ends." />
      ) : (
        <div className="oa-stack" aria-live="polite">
          {items.map(item => {
            const status = lookup(ACCESS_STATUS_META, item.status);
            const pending = item.status !== 'Removed';
            return (
              <article key={item.orderId} className="pk-card oa-panel" aria-labelledby={`access-${item.orderId}`}>
                <div className="oa-stack">
                  <div className="oa-panel__head">
                    <div>
                      <h2 className="oa-panel__title" id={`access-${item.orderId}`}>
                        <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(item.orderId)}`}>
                          {item.orderId}
                        </Link>
                      </h2>
                      <span className="oa-sub">
                        {[item.customer || 'Customer', item.email, item.gitHubUsername ? `@${item.gitHubUsername}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </div>
                    <span className="pk-badge" data-tone={status?.tone}>
                      {status?.label || item.status}
                    </span>
                  </div>

                  <ul className="oa-issue-list">
                    {(item.deliverables || []).map((d, index) => (
                      <li key={`${d.productName}-${index}`}>
                        {d.error ? <AlertTriangle aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
                        <span>
                          {d.productName} · expired {formatDateTime(d.accessExpiresAt)}
                          {d.accessRevokedAt ? ` · removed ${formatDateTime(d.accessRevokedAt)}` : ''}
                          {d.error && <span className="pk-error oa-small"> {d.error}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="oa-actions">
                    {pending && (
                      <Popconfirm
                        title="Remove access now?"
                        description="Download links stop working and GitHub repository access is removed. The customer is emailed."
                        okText="Remove access"
                        okDanger
                        disabled={!!busy}
                        onConfirm={() => run(`${item.orderId}:revoke`, () => fulfillmentService.revokeAccess(item.orderId))}
                      >
                        <button
                          type="button"
                          className="pk-btn pk-btn--danger pk-btn--sm"
                          disabled={!!busy}
                          aria-busy={busy === `${item.orderId}:revoke` || undefined}
                        >
                          <ShieldOff aria-hidden="true" /> {item.status === 'RemovalFailed' ? 'Retry removal' : 'Remove access'}
                        </button>
                      </Popconfirm>
                    )}
                    <button
                      type="button"
                      className="pk-btn pk-btn--secondary pk-btn--sm"
                      disabled={!!busy}
                      aria-busy={busy === `${item.orderId}:extend` || undefined}
                      onClick={() => run(`${item.orderId}:extend`, () => fulfillmentService.extendAccess(item.orderId, { days: 30 }))}
                    >
                      <CalendarPlus aria-hidden="true" /> {pending ? 'Extend 30 days' : 'Restore for 30 days'}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Every paid order that isn't fully delivered, with the reason, who has to act, what to do and the next
 * automatic retry. Most problems fix themselves (retries) or are fixed by the customer (GitHub username);
 * the rest can be fixed here.
 */
const DeliveryIssues = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(null);
  const [usernames, setUsernames] = useState({});

  const load = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    const result = await fulfillmentService.getNeedsAttention(200);
    if (result.success) {
      setData(result.data || { count: 0, needsAdmin: 0, waitingOnCustomer: 0, items: [] });
      setError('');
    } else if (initial) {
      setError(result.error);
    } else {
      toast.error(result.error);
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load({ initial: true });
  }, [load]);

  const retry = async item => {
    setRetrying(item.orderId);
    const gitHubUsername = lookup(usernames, item.orderId)?.trim();
    const result = await fulfillmentService.retryDelivery(item.orderId, gitHubUsername ? { gitHubUsername } : {});
    setRetrying(null);
    if (result.success) toast.success(result.message || 'Delivered');
    else toast.error({ content: result.message || result.error || 'Still not delivered', duration: 6 });
    load();
  };

  if (loading) return <Loading label="Loading delivery issues" />;

  const items = data?.items || [];

  return (
    <div className="oa-page">
      <AdminHeader
        title="Delivery issues"
        sub="Paid orders that aren't fully delivered. Failed deliveries retry automatically (5 minutes, then up to 24 hours apart) until the attempts run out; anything marked “needs you” waits for your action."
        actions={
          <button
            type="button"
            className="pk-btn pk-btn--ghost pk-btn--sm"
            onClick={() => load()}
            disabled={refreshing}
            aria-busy={refreshing || undefined}
          >
            <RefreshCw aria-hidden="true" /> Refresh
          </button>
        }
      />

      <Tabs
        items={[
          {
            key: 'delivery',
            label: `Delivery${data?.count ? ` (${data.count})` : ''}`,
            children: (
          error ? (
            <LoadError title="Couldn't load delivery issues" message={error} onRetry={() => load({ initial: true })} />
          ) : (
            <>
              <div className="oa-stats">
                <StatTile label="Orders listed" value={data?.count ?? 0} />
                <StatTile label="Need you" value={data?.needsAdmin ?? 0} attention={data?.needsAdmin > 0} />
                <StatTile label="Waiting on the customer" value={data?.waitingOnCustomer ?? 0} hint="Usually a missing GitHub username" />
              </div>

              {items.length === 0 ? (
                <EmptyState
                  icon={<PackageCheck />}
                  title="Every paid order is delivered"
                  text="New delivery problems show up here automatically."
                />
              ) : (
                <div className="oa-stack" aria-live="polite">
                  {items.map(item => {
                    const status = lookup(STATUS_META, item.status);
                    const customerOnly = item.actionBy === 'customer';
                    const awaitingAdmin = item.issues?.some(issue => issue.code === 'AWAITING_ADMIN');
                    const inputId = `retry-username-${item.orderId}`;
                    return (
                      <article
                        key={item.orderId}
                        className={`pk-card oa-panel oa-issue${customerOnly ? ' oa-issue--customer' : ''}`}
                        aria-labelledby={`issue-${item.orderId}`}
                      >
                        <div className="oa-stack">
                          <div className="oa-panel__head">
                            <div>
                              <h2 className="oa-panel__title" id={`issue-${item.orderId}`}>
                                <Link className="oa-link oa-mono" to={`/admin/orders/${encodeURIComponent(item.orderId)}`}>
                                  {item.orderId}
                                </Link>
                              </h2>
                              <span className="oa-sub">
                                {[item.customer || 'Customer', item.email, item.phone].filter(Boolean).join(' · ')}
                              </span>
                            </div>
                            <div className="oa-inline">
                              <span className="pk-badge" data-tone={status?.tone}>
                                {awaitingAdmin ? 'Paid, not assigned' : status?.label || item.status}
                              </span>
                              <span className="pk-badge" data-tone={customerOnly ? 'info' : 'danger'}>
                                {customerOnly ? 'Waiting on customer' : 'Needs you'}
                              </span>
                            </div>
                          </div>

                          <p className="oa-small">
                            {(item.products || []).join(', ') || 'No products'} · {formatINR(item.amount)} · paid{' '}
                            {formatDateTime(item.paidAt)}
                          </p>

                          {item.issues?.length > 0 && (
                            <ul className="oa-issue-list">
                              {item.issues.map((issue, index) => (
                                <li key={`${issue.code}-${index}`}>
                                  <AlertTriangle aria-hidden="true" />
                                  <span>
                                    {issue.message}{' '}
                                    <span className="oa-muted">({lookup(ACTORS, issue.actionBy) || issue.actionBy})</span>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}

                          {item.whatToDo && (
                            <div className="pk-alert" data-tone="info" role="note">
                              <Info aria-hidden="true" />
                              <div>
                                <strong>What to do:</strong> {item.whatToDo}
                              </div>
                            </div>
                          )}

                          <p className="oa-sub">
                            Attempts: {item.attempts ?? 0} · last {formatDateTime(item.lastAttemptAt)} · next automatic retry{' '}
                            {item.nextAttemptAt ? formatDateTime(item.nextAttemptAt) : 'none'}
                          </p>

                          <div className="oa-toolbar">
                            <div className="pk-field oa-grow">
                              <label className="pk-label" htmlFor={inputId}>
                                GitHub username <span className="pk-optional">(optional, corrects the customer's)</span>
                              </label>
                              <input
                                id={inputId}
                                className="pk-input oa-sm"
                                autoComplete="off"
                                spellCheck={false}
                                placeholder={item.gitHubUsername ? `Now @${item.gitHubUsername}` : 'github-username'}
                                value={lookup(usernames, item.orderId) || ''}
                                onChange={e => {
                                  const { value } = e.target;
                                  setUsernames(current => ({ ...current, [item.orderId]: value }));
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              className="pk-btn pk-btn--primary pk-btn--sm"
                              onClick={() => retry(item)}
                              disabled={retrying === item.orderId}
                              aria-busy={retrying === item.orderId || undefined}
                            >
                              {awaitingAdmin ? <PackageCheck aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}
                              {awaitingAdmin ? 'Assign deliverables' : 'Retry delivery now'}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )
            ),
          },
          { key: 'access', label: 'Expired access', children: <AccessChanges /> },
        ]}
      />
    </div>
  );
};

export default DeliveryIssues;
